


import React, { useState, useEffect } from "react";
import { db } from "../firebase/firebase";
import { collection, addDoc, query, onSnapshot } from "firebase/firestore";
import AIAssistant from "./AIAssistant";
import { Helmet } from "react-helmet-async";

export default function HeroBooking() {

  const servicePrices = {
    "Fade": "25€",
    "Taper": "20€",
    "Classic Cut": "18€",
    "Beard Trim": "10€"
  };

  const [appointments, setAppointments] = useState([]);
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    haircut: "",
    price: "",
    date: "",
    hour: "",
    minute: ""
  });

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "appointments"));
    const unsubscribe = onSnapshot(q, snapshot => {
      setAppointments(snapshot.docs.map(doc => doc.data()));
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value,
      price: name === "haircut" ? servicePrices[value] || "" : prev.price
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.phone || !form.haircut || !form.date || !form.hour || !form.minute) {
      setMessage("Please fill all fields.");
      return;
    }

    const time = `${form.hour}:${form.minute}`;

    const slotTaken = appointments.some(
      appt => appt.date === form.date && appt.time === time
    );

    if (slotTaken) {
      setMessage("This slot is already booked.");
      return;
    }

    try {
      await addDoc(collection(db, "appointments"), {
        ...form,
        time
      });

      setForm({
        name: "",
        phone: "",
        haircut: "",
        price: "",
        date: "",
        hour: "",
        minute: ""
      });

      setMessage("Appointment booked successfully.");
    } catch {
      setMessage("Booking failed. Try again.");
    }
  };

  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const startDay = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const changeMonth = (offset) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + offset);
      return d;
    });
  };

  const getStatus = (dateString) => {
    const count = appointments.filter(a => a.date === dateString).length;

    if (count === 0) return "free";
    if (count < 20) return "partial";
    return "full";
  };

  const generateTimeSlots = () => {
    const slots = [];

    for (let h = 9; h <= 18; h++) {
      ["00", "15", "30", "45"].forEach(m => {
        const hour = h < 10 ? "0" + h : "" + h;
        const time = `${hour}:${m}`;

        const taken = appointments.some(
          a => a.date === selectedDate && a.time === time
        );

        slots.push({ time, taken });
      });
    }

    return slots;
  };

  return (
    <section className="booking-section">

      {/* ===== SEO ===== */}
      <Helmet>
        <title>Salon SaaS Booking System Demo | ManieraDigital</title>

        <meta
          name="description"
          content="Live SaaS salon booking system with Firebase, AI assistant, calendar scheduling and automation."
        />

        <meta
          name="keywords"
          content="salon SaaS, booking system, salon software, Firebase booking, AI assistant salon"
        />

        <link rel="canonical" href="https://www.manieradigital.com/salon-software-demo" />

        <meta property="og:title" content="Salon SaaS Booking Demo" />
        <meta property="og:description" content="Real-time salon booking SaaS system demo." />
        <meta property="og:type" content="website" />

        <meta name="robots" content="index, follow" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Maniera Salon SaaS Demo",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description: "Live salon SaaS booking system with Firebase backend and AI assistant.",
            url: "https://www.manieradigital.com/salon-software-demo"
          })}
        </script>
      </Helmet>

      {/* ===== SAAS HEADER ===== */}
      <div className="saas-demo-header">
        <span className="badge-live">LIVE SAAS DEMO</span>

        <h1>Salon Booking SaaS System</h1>

        <p>
          Real-time booking system powered by Firebase with AI assistant,
          calendar scheduling and automated salon management.
        </p>

        <div className="saas-features">
          <div>⚡ Real-time Database</div>
          <div>🤖 AI Assistant</div>
          <div>📅 Smart Calendar</div>
          <div>💳 SaaS Architecture</div>
        </div>
      </div>

      {/* ===== TITLE ===== */}
      <h2 className="booking-title">Luxury Hair Salon Booking</h2>
      <p className="booking-subtitle">
        Book your appointment in seconds with a refined SaaS experience.
      </p>

      {/* ===== FORM ===== */}
      <form onSubmit={handleSubmit} className="booking-form">

        <input className="contact-input" name="name" placeholder="Your Name" value={form.name} onChange={handleChange} />
        <input className="contact-input" name="phone" placeholder="Phone Number" value={form.phone} onChange={handleChange} />

        <select className="contact-input" name="haircut" value={form.haircut} onChange={handleChange}>
          <option value="">Select Service</option>
          {Object.keys(servicePrices).map((s, i) => (
            <option key={i} value={s}>{s} ({servicePrices[s]})</option>
          ))}
        </select>

        <input className="contact-input" type="date" name="date" value={form.date} onChange={handleChange} />

        <div className="time-row">
          <select className="contact-input" name="hour" value={form.hour} onChange={handleChange}>
            <option value="">Hour</option>
            {Array.from({ length: 10 }, (_, i) => i + 9).map(h => (
              <option key={h} value={h < 10 ? "0" + h : h}>{h}</option>
            ))}
          </select>

          <select className="contact-input" name="minute" value={form.minute} onChange={handleChange}>
            <option value="">Min</option>
            {["00", "15", "30", "45"].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <button className="contact-btn">Book Appointment</button>
      </form>

      {message && <p className="booking-message">{message}</p>}

      {/* ===== CALENDAR ===== */}
      <div className="calendar">

        <div className="calendar-header">
          <button onClick={() => changeMonth(-1)}>◀</button>
          <h3>
            {currentDate.toLocaleString("default", { month: "long" })} {currentDate.getFullYear()}
          </h3>
          <button onClick={() => changeMonth(1)}>▶</button>
        </div>

        <div className="calendar-days">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d}>{d}</div>)}
        </div>

        <div className="calendar-grid">
          {Array.from({ length: startDay }).map((_, i) => <div key={i}></div>)}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const dateString = formatDate(dateObj);

            const status = getStatus(dateString);

            const colorClass =
              status === "free" ? "day-free" :
              status === "partial" ? "day-partial" : "day-full";

            return (
              <div
                key={i}
                onClick={() => setSelectedDate(dateString)}
                className={`calendar-day ${colorClass} ${selectedDate === dateString ? "selected" : ""}`}
              >
                {day}
              </div>
            );
          })}
        </div>

        <div className="legend">🟢 Free | 🔵 Partial | 🔴 Full</div>

        {selectedDate && (
          <div className="slots">
            <h3>{selectedDate} - Available Times</h3>

            <div className="slots-grid">
              {generateTimeSlots().map((slot, i) => (
                <div key={i} className={`slot ${slot.taken ? "taken" : "free"}`}>
                  {slot.time}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ===== AI ===== */}
      <AIAssistant bookedAppointments={appointments} />

      {/* ===== CTA ===== */}
      <div className="demo-cta">
        <h2>Want this SaaS system for your business?</h2>
        <p>We build full custom booking SaaS platforms with dashboard, AI and SEO.</p>

        <a href="/contact" className="hero-button primary">
          Get Your SaaS System
        </a>
      </div>

    </section>
  );
}
